import { useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CloudOff, RefreshCw, Wifi, X } from "lucide-react";
import { useOffline } from "@/context/OfflineContext";
import { Button } from "@/components/ui/button";

const STYLE = {
  online: { text: "Online", className: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  offline: { text: "Offline", className: "bg-slate-200 text-slate-700 ring-slate-300" },
  pending: { text: "Pending", className: "bg-amber-50 text-amber-700 ring-amber-200" },
  syncing: { text: "Syncing", className: "bg-cyan-50 text-cyan-700 ring-cyan-200" },
  failed: { text: "Sync Failed", className: "bg-red-50 text-red-600 ring-red-200" },
};

const short = (iso) => (iso ? iso.replace("T", " ").slice(0, 16) : "—");

/** Permanent connection + queue indicator, with a drawer to review pending work. */
export function ConnectionIndicator() {
  const offline = useOffline();
  const [open, setOpen] = useState(false);
  if (!offline) return null;

  const { status, pending, failed, conflicts, ops, flush, retryFailed, resolveConflict, discard } =
    offline;
  const style = STYLE[status];
  const label =
    status === "syncing" || status === "pending"
      ? `${status === "syncing" ? "Syncing" : "Pending"} — ${pending.length}`
      : status === "failed"
        ? `Sync Failed — Review (${failed.length + conflicts.length})`
        : status === "offline" && pending.length
          ? `Offline — ${pending.length} pending`
          : style.text;

  return (
    <>
      <button
        data-testid="connection-indicator"
        data-status={status}
        onClick={() => setOpen(true)}
        title="Connection and pending changes"
        className={`mono flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9.5px] uppercase tracking-wider ring-1 transition-colors ${style.className}`}
      >
        {status === "offline" ? (
          <CloudOff size={12} />
        ) : status === "failed" ? (
          <AlertTriangle size={12} />
        ) : status === "syncing" ? (
          <RefreshCw size={12} className="animate-spin" />
        ) : (
          <Wifi size={12} />
        )}
        <span className="hidden sm:inline">{label}</span>
      </button>

      {open
        ? createPortal(
            <div className="fixed inset-0 z-[60] flex justify-end bg-slate-900/30" onClick={() => setOpen(false)}>
              <aside
                data-testid="sync-drawer"
                onClick={(e) => e.stopPropagation()}
                className="okn-scroll h-full w-full max-w-md overflow-y-auto bg-white p-5 shadow-2xl"
              >
            <div className="flex items-start justify-between">
              <div>
                <p className="mono text-[10px] uppercase tracking-wider text-slate-500">
                  Connection
                </p>
                <h3 className="font-display text-lg font-semibold">{label}</h3>
              </div>
              <button data-testid="sync-drawer-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X size={18} />
              </button>
            </div>

            <div className="mt-4 flex gap-2">
              <Button
                data-testid="sync-now-button"
                onClick={() => flush()}
                className="h-8 rounded-full bg-cyan-500 px-3 text-[10px] uppercase tracking-wider text-[#04121a]"
              >
                <RefreshCw size={12} className="mr-1.5" /> Sync now
              </Button>
              {failed.length ? (
                <Button
                  data-testid="sync-retry-failed"
                  variant="outline"
                  onClick={retryFailed}
                  className="mono h-8 rounded-full border-slate-200 px-3 text-[10px] uppercase tracking-wider"
                >
                  Retry failed
                </Button>
              ) : null}
            </div>

            {ops.length === 0 ? (
              <p data-testid="sync-empty" className="mt-6 text-sm text-slate-500">
                Everything is synchronized. Updates made while offline will appear here.
              </p>
            ) : (
              <ul className="mt-5 space-y-2.5">
                {ops.map((op) => (
                  <li
                    key={op.op_id}
                    data-testid={`sync-op-${op.op_id}`}
                    className={`rounded-xl border p-3 ${
                      op.state === "conflict"
                        ? "border-amber-200 bg-amber-50/50"
                        : op.state === "failed"
                          ? "border-red-200 bg-red-50/50"
                          : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[12.5px] font-medium text-slate-800">{op.label}</span>
                      <span className="mono text-[9px] uppercase tracking-wider text-slate-500">
                        {op.state}
                      </span>
                    </div>
                    <p className="mono mt-0.5 text-[9.5px] uppercase tracking-wider text-slate-400">
                      {short(op.created_at)} · attempt {op.attempts}
                    </p>
                    {op.error ? (
                      <p className="mt-1 text-[11px] text-slate-600">{op.error}</p>
                    ) : null}
                    {op.state === "conflict" ? (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <Button
                          data-testid={`conflict-keep-server-${op.op_id}`}
                          variant="outline"
                          onClick={() => resolveConflict(op.op_id, "server")}
                          className="mono h-7 rounded-full border-slate-200 px-2.5 text-[9.5px] uppercase tracking-wider"
                        >
                          Keep server version
                        </Button>
                        <Button
                          data-testid={`conflict-reapply-${op.op_id}`}
                          onClick={() => resolveConflict(op.op_id, "mine")}
                          className="h-7 rounded-full bg-amber-500 px-2.5 text-[9.5px] uppercase tracking-wider text-[#1a1204]"
                        >
                          Reapply my change
                        </Button>
                      </div>
                    ) : (
                      <button
                        data-testid={`sync-discard-${op.op_id}`}
                        onClick={() => discard(op.op_id)}
                        className="mono mt-2 text-[9.5px] uppercase tracking-wider text-slate-400 hover:text-red-600"
                      >
                        Discard
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
              </aside>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
